import { ButtonHierarchy } from "@redotech/redo-model/content-builder/element-schema";
import {
  ButtonSettings,
  PageButtonSettings,
  TrackingPageSettings,
} from "@redotech/redo-model/order-tracking/tracking-page-definition";

const fontWeightMap: Record<string, number> = {
  thin: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

/**
 * Common block styling utilities for tracking page blocks
 */
export function getBlockStyles(pageSettings: TrackingPageSettings | undefined) {
  return {
    backgroundColor: pageSettings?.blockSettings.backgroundColor,
    borderRadius: pageSettings?.blockSettings.borderRadius,
    borderColor: pageSettings?.blockSettings.borderColor,
    fontFamily: pageSettings?.typography.bodyFontFamily,
    color: pageSettings?.typography.bodyFontColor,
  };
}

export function getHeaderStyles(
  pageSettings: TrackingPageSettings | undefined,
) {
  return {
    fontFamily: pageSettings?.typography.headerFontFamily,
    color: pageSettings?.typography.headerFontColor,
    ...(pageSettings?.typography.headerFontSize
      ? { fontSize: `${pageSettings.typography.headerFontSize}px` }
      : {}),
    ...(pageSettings?.typography.headerFontWeight
      ? { fontWeight: fontWeightMap[pageSettings.typography.headerFontWeight] }
      : {}),
  };
}

export function getBadgeStyles(pageSettings: TrackingPageSettings | undefined) {
  const badge = pageSettings?.badgeSettings;
  if (!badge) {
    return undefined;
  }
  return {
    backgroundColor: badge.backgroundColor,
    borderColor: badge.borderColor,
    color: badge.fontColor,
  };
}

export function getBodyStyles(pageSettings: TrackingPageSettings | undefined) {
  return {
    fontFamily: pageSettings?.typography.bodyFontFamily,
    color: pageSettings?.typography.bodyFontColor,
    ...(pageSettings?.typography.bodyFontSize
      ? { fontSize: `${pageSettings.typography.bodyFontSize}px` }
      : {}),
    ...(pageSettings?.typography.bodyFontWeight
      ? { fontWeight: fontWeightMap[pageSettings.typography.bodyFontWeight] }
      : {}),
  };
}

// label styles are just 60% opacity of body styles
export function getLabelStyles(pageSettings: TrackingPageSettings | undefined) {
  return {
    fontFamily: pageSettings?.typography.bodyFontFamily,
    color: pageSettings?.typography.bodyFontColor
      ? `${pageSettings.typography.bodyFontColor}99`
      : undefined,
    ...(pageSettings?.typography.bodyFontSize
      ? { fontSize: `${pageSettings.typography.bodyFontSize}px` }
      : {}),
    ...(pageSettings?.typography.bodyFontWeight
      ? { fontWeight: fontWeightMap[pageSettings.typography.bodyFontWeight] }
      : {}),
  };
}

export function getDeliveryDateStyles(
  pageSettings: TrackingPageSettings | undefined,
) {
  return pageSettings?.typography.deliveryDateFontSize
    ? {
        fontSize: `${pageSettings.typography.deliveryDateFontSize}px`,
        lineHeight: `${pageSettings.typography.deliveryDateFontSize}px`,
      }
    : {};
}

export function getButtonBorderRadius(
  pageSettings: TrackingPageSettings | undefined,
): Record<string, string> | undefined {
  if (pageSettings?.buttonBorderRadius === undefined) {
    return undefined;
  }
  return { borderRadius: `${pageSettings.buttonBorderRadius}px` };
}

export function getScrollbarHiddenStyles() {
  return {
    overflowY: "auto" as const,
    overflowX: "hidden" as const,
    scrollbarWidth: "none" as const,
    msOverflowStyle: "none" as const,
  };
}

export function getLinkStyles(pageSettings: TrackingPageSettings | undefined) {
  return { color: pageSettings?.typography.linkFontColor };
}

export function getTrackingNumberUrlStyles(
  pageSettings: TrackingPageSettings | undefined,
) {
  return {
    color: pageSettings?.typography.linkFontColor,
    ...(pageSettings?.typography.trackingNumberUrlFontSize
      ? { fontSize: `${pageSettings.typography.trackingNumberUrlFontSize}px` }
      : {}),
  };
}

/**
 * Get button styles for a specific hierarchy, prioritizing block-level settings
 *
 * @param hierarchy - The button hierarchy (primary, secondary, or tertiary)
 * @param buttonSettings - Block-level button settings
 * @returns Style object with backgroundColor, color, outlineColor, and CSS variables for hover/active states
 */
export function getButtonStyles(
  pageSettings: PageButtonSettings | undefined,
  buttonSettings: ButtonSettings | undefined,
) {
  if (buttonSettings?.hierarchy === "custom") {
    return {
      backgroundColor:
        buttonSettings?.buttonConfiguration.default.style.background,
      color: buttonSettings?.buttonConfiguration.default.text.fontColor,
      outlineColor: buttonSettings?.buttonConfiguration.default.style.border,
      ["--button-bg-hover"]:
        buttonSettings?.buttonConfiguration.hovered?.style.background,
      ["--button-color-hover"]:
        buttonSettings?.buttonConfiguration.hovered?.text.fontColor,
      ["--button-outline-hover"]:
        buttonSettings?.buttonConfiguration.hovered?.style.border,
      ["--button-bg-active"]:
        buttonSettings?.buttonConfiguration.active?.style.background,
      ["--button-color-active"]:
        buttonSettings?.buttonConfiguration.active?.text.fontColor,
      ["--button-outline-active"]:
        buttonSettings?.buttonConfiguration.active?.style.border,
    };
  }
  const settings = pageSettings?.[buttonSettings?.hierarchy ?? "primary"];
  return {
    backgroundColor: settings?.default.style.background,
    color: settings?.default.text.fontColor,
    outlineColor: settings?.default.style.border,
    ["--button-bg-hover"]: settings?.hovered?.style.background,
    ["--button-color-hover"]: settings?.hovered?.text.fontColor,
    ["--button-outline-hover"]: settings?.hovered?.style.border,
    ["--button-bg-active"]: settings?.active?.style.background,
    ["--button-color-active"]: settings?.active?.text.fontColor,
    ["--button-outline-active"]: settings?.active?.style.border,
  };
}

export function getButtonHierarchy(
  buttonSettings: ButtonSettings | undefined,
): ButtonHierarchy {
  if (!buttonSettings?.hierarchy) {
    return "primary";
  }
  return buttonSettings?.hierarchy !== "custom"
    ? buttonSettings?.hierarchy
    : "primary";
}
